import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      // Let login pages handle invalid credentials — do not redirect away
      if (requestUrl.includes('/login')) {
        return Promise.reject(error);
      }
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const onCustomerLogin = window.location.pathname.startsWith('/customer');
      const isCustomer = user.role === 'customer' || onCustomerLogin || sessionStorage.getItem('loginContext') === 'customer';
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = isCustomer ? '/customer/login' : '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username, password) =>
  api.post('/api/admin/login', { username, password });

export const logout = () =>
  api.post('/api/admin/logout');

export const verifyToken = () =>
  api.get('/api/admin/verify');

// Stats
export const getStats = () =>
  api.get('/api/admin/stats');

export const getProviderHealth = () =>
  api.get('/api/admin/provider-health');

// Providers
export const getProviders = () =>
  api.get('/api/providers');

export const createProvider = (data) =>
  api.post('/api/providers', data);

export const updateProvider = (id, data) =>
  api.put(`/api/providers/${id}`, data);

export const deleteProvider = (id) =>
  api.delete(`/api/providers/${id}`);

export const testProvider = (id) =>
  api.post(`/api/providers/${id}/test`);

export const syncProvider = (id) =>
  api.post(`/api/providers/${id}/sync`);

// Channels
export const getChannels = (params) =>
  api.get('/api/channels', { params });

export const getChannelCategories = (params) =>
  api.get('/api/channels/categories', { params });

export const updateChannel = (id, data) =>
  api.put(`/api/channels/${id}`, data);

export const bulkUpdateChannels = (data) =>
  api.post('/api/channels/bulk-update-v2', data);

export const bulkEnableChannels = (params) =>
  api.put('/api/channels/bulk-enable', undefined, { params });

export const bulkDisableChannels = (params) =>
  api.put('/api/channels/bulk-disable', undefined, { params });

export const bulkDeleteChannels = (data) =>
  api.post('/api/channels/bulk-delete', data);

export const enableProviderContent = (providerId) =>
  api.put(`/api/channels/provider/${providerId}/enable-all`);

export const disableProviderContent = (providerId) =>
  api.put(`/api/channels/provider/${providerId}/disable-all`);

export const deleteChannel = (id) =>
  api.delete(`/api/channels/${id}`);

// VOD
export const getVod = (params) =>
  api.get('/api/vod', { params });

export const getVodCategories = (params) =>
  api.get('/api/vod/categories', { params });

export const updateVod = (id, data) =>
  api.put(`/api/vod/${id}`, data);

export const bulkEnableVod = (params) =>
  api.put('/api/vod/bulk-enable', undefined, { params });

export const bulkDisableVod = (params) =>
  api.put('/api/vod/bulk-disable', undefined, { params });

export const bulkUpdateVod = (data) =>
  api.post('/api/vod/bulk-update', data);

export const bulkDeleteVod = (data) =>
  api.post('/api/vod/bulk-delete', data);

export const deleteVod = (id) =>
  api.delete(`/api/vod/${id}`);

// Series
export const getSeries = (params) =>
  api.get('/api/series', { params });

export const getSeriesCategories = (params) =>
  api.get('/api/series/categories', { params });

export const updateSeries = (id, data) =>
  api.put(`/api/series/${id}`, data);

export const bulkEnableSeries = (params) =>
  api.put('/api/series/bulk-enable', undefined, { params });

export const bulkDisableSeries = (params) =>
  api.put('/api/series/bulk-disable', undefined, { params });

export const bulkUpdateSeries = (data) =>
  api.post('/api/series/bulk-update', data);

export const bulkDeleteSeries = (data) =>
  api.post('/api/series/bulk-delete', data);

export const deleteSeries = (id) =>
  api.delete(`/api/series/${id}`);

// EPG
export const getEpgSources = () =>
  api.get('/api/epg/sources');

export const createEpgSource = (data) =>
  api.post('/api/epg/sources', data);

export const updateEpgSource = (id, data) =>
  api.put(`/api/epg/sources/${id}`, data);

export const deleteEpgSource = (id) =>
  api.delete(`/api/epg/sources/${id}`);

export const fetchEpgSource = (id) =>
  api.post(`/api/epg/sources/${id}/fetch`);

export const getEpgCoverage = () =>
  api.get('/api/epg/coverage');

export const getUnmatchedChannels = (params) =>
  api.get('/api/epg/unmatched', { params });

export const createEpgMapping = (data) =>
  api.post('/api/epg/match', data);

export const autoMatchEpg = () =>
  api.post('/api/epg/auto-match');

// Users
export const getUsers = () =>
  api.get('/api/users');

export const getUser = (id) =>
  api.get(`/api/users/${id}`);

export const createUser = (data) =>
  api.post('/api/users', data);

export const updateUser = (id, data) =>
  api.put(`/api/users/${id}`, data);

export const toggleUser = (id) =>
  api.put(`/api/users/${id}/toggle`);

export const deleteUser = (id) =>
  api.delete(`/api/users/${id}`);

export const resetUserPassword = (id, new_password) =>
  api.post(`/api/users/${id}/reset-password`, { new_password });

export const getUserConnectionInfo = (id) =>
  api.get(`/api/users/${id}/connection-info`);

// Custom Categories
export const getCustomCategories = (params) =>
  api.get('/api/custom-categories', { params });

export const getCustomCategoryItems = (id) =>
  api.get(`/api/custom-categories/${id}`);

export const createCustomCategory = (data) =>
  api.post('/api/custom-categories', data);

export const updateCustomCategory = (id, data) =>
  api.put(`/api/custom-categories/${id}`, data);

export const deleteCustomCategory = (id) =>
  api.delete(`/api/custom-categories/${id}`);

export const addItemsToCategory = (id, items) =>
  api.post(`/api/custom-categories/${id}/items`, { items });

export const removeItemFromCategory = (categoryId, mappingId) =>
  api.delete(`/api/custom-categories/${categoryId}/items/${mappingId}`);

export const getAvailableItems = (categoryId, type, search) =>
  api.get(`/api/custom-categories/${categoryId}/available-items`, {
    params: { type, search }
  });

// Settings
export const changeAdminPassword = (data) =>
  api.post('/api/admin/change-password', data);

export const getSystemLogs = () =>
  api.get('/api/admin/logs');

export const runDiagnostic = () =>
  api.post('/api/admin/diagnostic');

export const getDiagnostic = (ref) =>
  api.get(`/api/admin/diagnostic/${ref}`);

export const listDiagnostics = (limit = 10) =>
  api.get(`/api/admin/diagnostics?limit=${limit}`);

// ─── Customer Management (Admin) ───────────────────────────────────────────
export const getCustomers = () => api.get('/api/admin/customers');
export const getCustomer = (id) => api.get(`/api/admin/customers/${id}`);
export const createCustomer = (data) => api.post('/api/admin/customers', data);
export const updateCustomer = (id, data) => api.put(`/api/admin/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/api/admin/customers/${id}`);
export const updateCustomerProviders = (id, provider_ids) => api.put(`/api/admin/customers/${id}/providers`, { provider_ids });

// ─── Customer Portal ───────────────────────────────────────────────────────
export const customerLogin = (username, password) => api.post('/api/customer/login', { username, password });
export const customerLogout = () => api.post('/api/customer/logout');
export const customerGetProfile = () => api.get('/api/customer/me');
export const customerChangePassword = (currentPassword, newPassword) => api.put('/api/customer/me/password', { currentPassword, newPassword });
export const customerUpdateXtream = (xtream_username, xtream_password) => api.put('/api/customer/me/xtream', { xtream_username, xtream_password });
export const customerGetConnectionInfo = () => api.get('/api/customer/me/connection-info');
export const customerSubmitDiagnostic = () => api.post('/api/customer/diagnostic');
export const customerEpgAutoMatch = () => api.post('/api/customer/epg/auto-match');
export const customerEpgAutoMatchStatus = () => api.get('/api/customer/epg/auto-match/status');

export const customerGetChannels = (params) => api.get('/api/customer/channels', { params });
export const customerGetMovies = (params) => api.get('/api/customer/movies', { params });
export const customerGetSeries = (params) => api.get('/api/customer/series', { params });
export const customerGetCategories = (type, provider_id) => api.get(`/api/customer/categories/${type}`, { params: provider_id ? { provider_id } : {} });
export const customerToggleChannel = (id) => api.put(`/api/customer/channels/${id}/toggle`);
export const customerToggleMovie = (id) => api.put(`/api/customer/movies/${id}/toggle`);
export const customerToggleSeries = (id) => api.put(`/api/customer/series/${id}/toggle`);
export const customerBulkToggleChannels = (item_ids, enabled) => api.put('/api/customer/channels/bulk-toggle', { item_ids, enabled });
export const customerBulkToggleChannelsByFilter = (opts) => api.put('/api/customer/channels/bulk-toggle', opts);export const customerBulkToggleMoviesByFilter = (opts) => api.put('/api/customer/movies/bulk-toggle', opts);export const customerBulkToggleSeriesByFilter = (opts) => api.put('/api/customer/series/bulk-toggle', opts);
export const customerBulkToggleMovies = (item_ids, enabled) => api.put('/api/customer/movies/bulk-toggle', { item_ids, enabled });
export const customerBulkToggleSeries = (item_ids, enabled) => api.put('/api/customer/series/bulk-toggle', { item_ids, enabled });

export const customerGetOnboardingStatus = () => api.get('/api/customer/onboarding/status');
export const customerCompleteOnboarding = (data) => api.post('/api/customer/onboarding/complete', data);
export const customerApplyStarterPack = (pack_id) => api.post('/api/customer/onboarding/starter-pack', { pack_id });
export const customerGetProviderStatus = () => api.get('/api/customer/providers/status');
export const customerSetPrimaryProviderLine = (line_id) => api.put('/api/customer/me/provider-lines/primary', { line_id });
export const adminSetPrimaryProviderLine = (customerId, lineId) => api.put(`/api/admin/customers/${customerId}/provider-lines/${lineId}/primary`);
export const customerGetFailoverHealth = () => api.get('/api/customer/failover-health');
export const customerToggleFavorite = (item_type, item_id) => api.post('/api/customer/favorites/toggle', { item_type, item_id });
export const customerGetFavorites = () => api.get('/api/customer/favorites');
export const customerGetDuplicates = (type, params) => api.get(`/api/customer/duplicates/${type}`, { params });
export const customerResolveDuplicate = (data) => api.post('/api/customer/duplicates/resolve', data);
export const getCustomerUsage = () => api.get('/api/admin/customer-usage');
export const getAdminFailoverHealth = () => api.get('/api/admin/failover-health');
export const runStreamProbes = () => api.post('/api/admin/stream-probes/run');
export const getServerStatus = () => api.get('/api/admin/server-status');

// Phase 3: Observability endpoints
export const getCustomerSyncHistory = (id) => api.get(`/api/admin/customers/${id}/sync-history`);
export const getFailoverCleanupStats = () => api.get("/api/admin/failover-cleanup-stats");
export const getEpgFreshness = () => api.get("/api/admin/epg-freshness");
export const customerSearch = (q) => api.get("/api/customer/search", { params: { q } });

export default api;
